import React from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";

const RISK_LEVEL_LABELS = {
  10: "SAFE",
  35: "ELEVATED",
  70: "WARNING",
  95: "CRITICAL",
};

const CustomTooltip = ({ active, payload, label, t }) => {
  if (!active || !payload || !payload.length) return null;

  const riskValue = payload.find((p) => p.dataKey === "risk")?.value;
  const confValue = payload.find((p) => p.dataKey === "confidence")?.value;
  const riskLabel = RISK_LEVEL_LABELS[riskValue] || (riskValue > 70 ? "WARNING" : "SAFE");

  return (
    <div style={{
      background: "#0f172a",
      border: "1px solid #1e293b",
      borderRadius: 8,
      padding: "10px 14px",
      fontSize: 13,
      color: "#e2e8f0",
    }}>
      <div style={{ marginBottom: 6, color: "#94a3b8" }}>{t("commandCenter.liveThreatAnalytics.readingNumber", { label })}</div>
      {riskValue !== undefined && (
        <div style={{ color: riskValue >= 95 ? "#dc2626" : riskValue >= 70 ? "#f59e0b" : riskValue >= 35 ? "#38bdf8" : "#22c55e", fontWeight: 700 }}>
          {t("commandCenter.liveThreatAnalytics.riskLabel")}: {t(`commandCenter.risk.${riskLabel}`)}
        </div>
      )}
      {confValue !== undefined && (
        <div style={{ color: "#60a5fa" }}>
          {t("commandCenter.liveThreatAnalytics.confidence")}: {confValue}%
        </div>
      )}
    </div>
  );
};

export default function LiveThreatAnalytics({ analyticsData = [], cardStyle = {} }) {
  const { t } = useTranslation();
  // If no data yet, show placeholder readings
  const displayData = analyticsData.length > 0 ? analyticsData : [
    { name: 1, confidence: 50, risk: 10 },
    { name: 2, confidence: 65, risk: 35 },
    { name: 3, confidence: 75, risk: 70 },
    { name: 4, confidence: 92, risk: 95 },
    { name: 5, confidence: 75, risk: 70 },
    { name: 6, confidence: 92, risk: 95 },
    { name: 7, confidence: 75, risk: 70 },
    { name: 8, confidence: 92, risk: 95 },
    { name: 9, confidence: 50, risk: 10 },
    { name: 10, confidence: 75, risk: 70 },
  ];

  const latestRisk = displayData[displayData.length - 1]?.risk || 10;
  const latestConf = displayData[displayData.length - 1]?.confidence || 50;
  const riskLabel =
    latestRisk >= 95 ? "CRITICAL" :
    latestRisk >= 70 ? "WARNING" :
    latestRisk >= 35 ? "ELEVATED" : "SAFE";
  const riskColor =
    latestRisk >= 95 ? "#dc2626" :
    latestRisk >= 70 ? "#f59e0b" :
    latestRisk >= 35 ? "#38bdf8" : "#22c55e";

  return (
    <div style={{
      ...cardStyle,
      gridColumn: "1 / -1", // ← FULL WIDTH across all columns
    }}>

      {/* HEADER ROW */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0 }}>📈 {t("commandCenter.liveThreatAnalytics.title")}</h3>
          <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "#64748b" }}>
            {t("commandCenter.liveThreatAnalytics.subtitle", { count: displayData.length })}
          </p>
        </div>

        {/* CURRENT STATUS BADGES */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{
            padding: "6px 14px",
            borderRadius: 8,
            background: `${riskColor}18`,
            border: `1px solid ${riskColor}44`,
            textAlign: "center",
          }}>
            <div style={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>{t("commandCenter.liveThreatAnalytics.currentRisk")}</div>
            <div style={{ fontWeight: 700, color: riskColor, fontSize: "1rem" }}>{t(`commandCenter.risk.${riskLabel}`)}</div>
          </div>
          <div style={{
            padding: "6px 14px",
            borderRadius: 8,
            background: "rgba(96,165,250,0.1)",
            border: "1px solid rgba(96,165,250,0.3)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>{t("commandCenter.liveThreatAnalytics.confidence")}</div>
            <div style={{ fontWeight: 700, color: "#60a5fa", fontSize: "1rem" }}>{latestConf}%</div>
          </div>
        </div>
      </div>

      {/* CHART */}
      <ResponsiveContainer width="100%" height={280}>
        <LineChart
          data={displayData}
          margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />

          <XAxis
            dataKey="name"
            stroke="#475569"
            tick={{ fill: "#64748b", fontSize: 12 }}
            label={{ value: t("commandCenter.liveThreatAnalytics.readingsAxis"), position: "insideBottom", offset: -5, fill: "#475569", fontSize: 11 }}
          />

          <YAxis
            stroke="#475569"
            tick={{ fill: "#64748b", fontSize: 12 }}
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
          />

          <Tooltip content={<CustomTooltip t={t} />} />

          <Legend
            wrapperStyle={{ paddingTop: 12, fontSize: 13, color: "#94a3b8" }}
            formatter={(value) => value === "risk" ? t("commandCenter.liveThreatAnalytics.riskLevelLegend") : t("commandCenter.liveThreatAnalytics.aiConfidenceLegend")}
          />

          {/* Reference lines for risk thresholds */}
          <ReferenceLine y={95} stroke="#dc262640" strokeDasharray="4 4" label={{ value: t("commandCenter.risk.CRITICAL"), fill: "#dc2626", fontSize: 10, position: "right" }} />
          <ReferenceLine y={70} stroke="#f59e0b40" strokeDasharray="4 4" label={{ value: t("commandCenter.risk.WARNING"), fill: "#f59e0b", fontSize: 10, position: "right" }} />
          <ReferenceLine y={35} stroke="#38bdf840" strokeDasharray="4 4" label={{ value: t("commandCenter.risk.ELEVATED"), fill: "#38bdf8", fontSize: 10, position: "right" }} />

          <Line
            type="monotone"
            dataKey="risk"
            stroke="#ef4444"
            strokeWidth={2.5}
            dot={{ fill: "#ef4444", r: 4, strokeWidth: 0 }}
            activeDot={{ r: 6, stroke: "#ef4444", strokeWidth: 2 }}
            name="risk"
          />

          <Line
            type="monotone"
            dataKey="confidence"
            stroke="#38bdf8"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={{ fill: "#38bdf8", r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5, stroke: "#38bdf8", strokeWidth: 2 }}
            name="confidence"
          />

        </LineChart>
      </ResponsiveContainer>

      {/* LEGEND EXPLANATION */}
      <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "#64748b" }}>
          <div style={{ width: 24, height: 3, background: "#ef4444", borderRadius: 2 }} />
          {t("commandCenter.liveThreatAnalytics.riskLevelLegendFull")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "#64748b" }}>
          <div style={{ width: 24, height: 2, background: "#38bdf8", borderRadius: 2, borderTop: "2px dashed #38bdf8" }} />
          {t("commandCenter.liveThreatAnalytics.aiConfidencePercent")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "#64748b" }}>
          <div style={{ width: 16, height: 2, background: "#dc262640", borderTop: "1px dashed #dc2626" }} />
          {t("commandCenter.liveThreatAnalytics.thresholdMarkers")}
        </div>
      </div>

    </div>
  );
}
